import apiService from '../services/APIService';
import Response from '../modules/classes/Response';

/**
 * Fetch profile stats (followers, following, views) in a mobile-like way.
 * - Tries dedicated endpoint /users/:id/stats if present
 * - Falls back to /users/:id/followers and /users/:id/following and reads total from payload
 * - Views will be taken from provided fallbackViews param or 0
 */
export default class ProfileStatsController {
  constructor(api) { this.api = api; }
  getService() { return this.api || apiService; }

  async incrementView(userId) {
    try {
      await this.getService().post(`/users/${userId}/view`);
    } catch (_) {}
  }

  async getStats(userId, fallbackViews = 0) {
    // Preferred: dedicated stats endpoint from FollowController
    try {
      const res = await this.getService().get(`/follow/stats/${userId}`);
      const payload = res?.data || res || {};
      const followers = payload.followers ?? payload.followersCount ?? 0;
      const following = payload.following ?? payload.followingCount ?? 0;
      // Also query profile for views to ensure non-zero when stats do not return views
      try {
        const profRes = await this.getService().get(`/users/${userId}/profile`);
        const profPayload = profRes?.data || profRes || {};
        const profile = profPayload?.profile || profPayload?.data?.profile || {};
        const views = typeof profile.views === 'number' ? profile.views : (payload.views ?? payload.lifetimeViews ?? fallbackViews ?? 0);
        return Response.success({ followers, following, views });
      } catch (_) {
        const views = payload.views ?? payload.lifetimeViews ?? fallbackViews ?? 0;
        return Response.success({ followers, following, views });
      }
    } catch (_) {}

    // Second: pull from profile (provides views in ProfileDto)
    try {
      const res = await this.getService().get(`/users/${userId}/profile`);
      const payload = res?.data || res || {};
      const profile = payload?.profile || payload?.data?.profile || {};
      const views = typeof profile.views === 'number' ? profile.views : (fallbackViews ?? 0);
      // Followers/following may not be present here
      return Response.success({ followers: 0, following: 0, views });
    } catch (_) {}

    // Fallback: query follower/following lists and read total/length
    const [followersRes, followingRes] = await Promise.allSettled([
      this.getService().get(`/follow/followers/${userId}`),
      this.getService().get(`/follow/following/${userId}`)
    ]);

    const parseTotal = (r) => {
      if (r?.status !== 'fulfilled') return 0;
      const data = r.value?.data || r.value;
      if (!data) return 0;
      // Common shapes: { data: { total, items } } or { total, items } or array
      if (typeof data.total === 'number') return data.total;
      if (typeof data?.data?.total === 'number') return data.data.total;
      if (Array.isArray(data.items)) return data.items.length;
      if (Array.isArray(data.data)) return data.data.length;
      if (Array.isArray(data)) return data.length;
      return 0;
    };

    const followers = parseTotal(followersRes);
    const following = parseTotal(followingRes);
    const views = fallbackViews ?? 0;

    return Response.success({ followers, following, views });
  }
}
